import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckSquare, ShieldCheck, Square } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Input, Label, PageHeader, Textarea } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Link } from "../lib/router";
import type { AccessRequestDetail } from "../types/api";

function parseList(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Monta a lista de itens a validar a partir do que o cliente efetivamente pediu
// nesta solicitação — nada de digitar de novo, a equipe só marca o que já fez.
function buildItensChecklist(data: AccessRequestDetail): string[] {
  const itens: string[] = [];
  if (data.tipo === "criacao") {
    itens.push(...parseList(data.AcessosCriar));
    if (data.AcessosPastas?.trim()) itens.push(`Acesso às pastas: ${data.AcessosPastas.trim()}`);
  } else {
    itens.push(...parseList(data.AcessosRemover).map((item) => `Remover acesso: ${item}`));
    itens.push("Conta desativada/bloqueada");
    if (data.BackupEmail) itens.push("Backup do e-mail realizado");
    if (data.RedirecionarEmail) itens.push(`Redirecionar e-mail para ${data.EmailRedirecionamento || "—"}`);
    if (data.RespostaAutomatica) itens.push("Resposta automática configurada");
  }
  return itens;
}

function SimNao({ value }: { value: boolean | null }) {
  if (value === null || value === undefined) return <span className="text-slate-400">Não informado</span>;
  return value ? <span className="text-emerald-600">Sim</span> : <span className="text-red-600">Não</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-0.5 text-sm text-slate-700">{children || "—"}</div>
    </div>
  );
}

export function AccessRequestDetailPage({ id }: { id: string }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [tecnico, setTecnico] = useState(session?.login ?? "");
  const [observacoes, setObservacoes] = useState("");
  const [itensMarcados, setItensMarcados] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);

  const solicitacao = useQuery({
    queryKey: ["access-request", id],
    queryFn: async () => {
      const rows = await api.getAccessRequest(Number(id));
      return rows[0] ?? null;
    },
  });

  const data = solicitacao.data;
  const itensChecklist = useMemo(() => (data ? buildItensChecklist(data) : []), [data]);

  useEffect(() => {
    if (!data) return;
    if (data.TecnicoValidacao) setTecnico(data.TecnicoValidacao);
    if (data.ObservacoesValidacao) setObservacoes(data.ObservacoesValidacao);
    const jaMarcados = parseList(data.ItensValidados);
    if (jaMarcados.length > 0) setItensMarcados(new Set(jaMarcados));
  }, [data]);

  function toggleItem(item: string) {
    setItensMarcados((current) => {
      const next = new Set(current);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  }

  async function concluirValidacao() {
    if (!tecnico.trim()) {
      toast.error("Informe quem está validando.");
      return;
    }
    if (itensChecklist.length > 0 && itensMarcados.size < itensChecklist.length) {
      toast.error("Marque todos os itens da lista antes de confirmar a validação.");
      return;
    }
    setSalvando(true);
    try {
      await api.validateAccessRequest(Number(id), {
        status: "concluido",
        tecnico,
        observacoes: observacoes || null,
        itensConcluidos: Array.from(itensMarcados),
      });
      toast.success("Validação concluída! O chamado vinculado foi encerrado automaticamente.");
      queryClient.invalidateQueries({ queryKey: ["access-request", id] });
    } catch {
      toast.error("Não foi possível concluir a validação. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  async function salvarObservacoes() {
    setSalvando(true);
    try {
      await api.validateAccessRequest(Number(id), {
        status: "pendente",
        tecnico,
        observacoes: observacoes || null,
        itensConcluidos: Array.from(itensMarcados),
      });
      toast.success("Progresso salvo.");
      queryClient.invalidateQueries({ queryKey: ["access-request", id] });
    } catch {
      toast.error("Não foi possível salvar. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="page-stack max-w-[960px]">
      <PageHeader
        eyebrow="Equipe Mira Sistemas"
        title={data ? `Solicitação de acesso #${data.Id}` : "Solicitação de acesso"}
        description="Conferência da solicitação registrada pela equipe."
        actions={
          <Link to="/checklist-formatacao/lista" className="button button--secondary">
            <ArrowLeft className="size-4" />
            Voltar à lista
          </Link>
        }
      />

      {solicitacao.isLoading && <p className="text-sm text-slate-500">Carregando...</p>}
      {solicitacao.isError && <p className="text-sm text-red-600">Não foi possível carregar esta solicitação.</p>}

      {data && (
        <div className="space-y-5">
          <Card className="form-section border-2 border-[var(--brand)]">
            <div className="form-section-heading">
              <span><ShieldCheck className="size-4" /></span>
              <div>
                <h2>Validação da equipe</h2>
                <p className="text-xs text-slate-500">
                  {data.StatusValidacao === "concluido"
                    ? `Validado por ${data.TecnicoValidacao ?? "—"} em ${data.DataValidacao ? new Date(data.DataValidacao).toLocaleString("pt-BR") : "—"}.`
                    : "Confirme que os itens solicitados acima foram executados antes de encerrar o chamado."}
                </p>
              </div>
            </div>

            {data.StatusValidacao === "concluido" ? (
              <>
                <span className="status-badge status-badge--success">Validação concluída</span>
                {itensChecklist.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {itensChecklist.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckSquare className="size-4 shrink-0 text-emerald-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                {itensChecklist.length > 0 && (
                  <div className="mb-4">
                    <Label>Itens solicitados — marque o que já foi feito</Label>
                    <ul className="mt-1 space-y-1">
                      {itensChecklist.map((item) => {
                        const marcado = itensMarcados.has(item);
                        return (
                          <li key={item}>
                            <button
                              type="button"
                              onClick={() => toggleItem(item)}
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                            >
                              {marcado ? (
                                <CheckSquare className="size-4 shrink-0 text-emerald-600" />
                              ) : (
                                <Square className="size-4 shrink-0 text-slate-300" />
                              )}
                              <span className={marcado ? "text-slate-700 line-through decoration-slate-300" : "text-slate-700"}>
                                {item}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-1 text-xs text-slate-400">
                      {itensMarcados.size}/{itensChecklist.length} itens marcados
                    </p>
                  </div>
                )}
                <div className="form-grid">
                  <div>
                    <Label htmlFor="val-tecnico">Técnico responsável pela validação</Label>
                    <Input
                      id="val-tecnico"
                      value={tecnico}
                      onChange={(event) => setTecnico(event.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="val-obs">Observações da validação</Label>
                  <Textarea
                    id="val-obs"
                    value={observacoes}
                    onChange={(event) => setObservacoes(event.target.value)}
                    placeholder="Ex.: acesso testado e confirmado com o usuário."
                  />
                </div>
                <div className="mt-4 flex flex-row gap-2">
                  <Button type="button" variant="secondary" disabled={salvando} onClick={salvarObservacoes}>
                    Salvar observações
                  </Button>
                  <Button type="button" disabled={salvando} onClick={concluirValidacao}>
                    <ShieldCheck className="size-4" />
                    Confirmar validação e liberar encerramento
                  </Button>
                </div>
              </>
            )}
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>01</span>
              <div>
                <h2>Solicitante</h2>
              </div>
            </div>
            <div className="form-grid">
              <Field label="Tipo">{data.tipo === "criacao" ? "Criação de usuário" : "Desativação de usuário"}</Field>
              <Field label="Chamado">{data.ChamadoId ? `#${data.ChamadoId}` : null}</Field>
              <Field label="Nome">{data.NomeSolicitante}</Field>
              <Field label="E-mail">{data.EmailSolicitante}</Field>
              <Field label="Empresa">{data.EmpresaSolicitante}</Field>
              <Field label="Departamento">{data.DepartamentoSolicitante}</Field>
              <Field label="Data da solicitação">
                {new Date(data.DataSolicitacao).toLocaleDateString("pt-BR")}
              </Field>
              <Field label="Registrado em">{new Date(data.DataCriacao).toLocaleString("pt-BR")}</Field>
            </div>
          </Card>

          {data.tipo === "criacao" ? (
            <Card className="form-section">
              <div className="form-section-heading">
                <span>02</span>
                <div>
                  <h2>Criação de novo usuário</h2>
                </div>
              </div>
              <div className="form-grid">
                <Field label="Nome do colaborador">{data.NomeColaborador}</Field>
                <Field label="E-mail do colaborador">{data.EmailColaborador}</Field>
                <Field label="Departamento">{data.DepartamentoColaborador}</Field>
                <Field label="Data de início">
                  {data.DataInicio ? new Date(data.DataInicio).toLocaleDateString("pt-BR") : null}
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Acessos a criar">{parseList(data.AcessosCriar).join(", ")}</Field>
              </div>
              <div className="mt-4">
                <Field label="Acessos às pastas">{data.AcessosPastas}</Field>
              </div>
            </Card>
          ) : (
            <Card className="form-section">
              <div className="form-section-heading">
                <span>02</span>
                <div>
                  <h2>Desativação de usuário</h2>
                </div>
              </div>
              <div className="form-grid">
                <Field label="Nome do colaborador">{data.NomeDesativado}</Field>
                <Field label="E-mail do colaborador">{data.EmailDesativado}</Field>
                <Field label="Data de saída">
                  {data.DataSaida ? new Date(data.DataSaida).toLocaleDateString("pt-BR") : null}
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Acessos a remover">{parseList(data.AcessosRemover).join(", ")}</Field>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Backup do e-mail"><SimNao value={data.BackupEmail} /></Field>
                <Field label="Redirecionar e-mail"><SimNao value={data.RedirecionarEmail} /></Field>
              </div>
              {data.RedirecionarEmail && (
                <div className="mt-4">
                  <Field label="E-mail de redirecionamento">{data.EmailRedirecionamento}</Field>
                </div>
              )}
              <div className="mt-4">
                <Field label="Resposta automática"><SimNao value={data.RespostaAutomatica} /></Field>
              </div>
              {data.RespostaAutomatica && (
                <div className="mt-4">
                  <Field label="Texto da resposta automática">{data.TextoRespostaAutomatica}</Field>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
